/**
 * Hook to call a selection of tracking events, including Segment, Google Analytics and Branch.
 *
 * Google Analytics is used for page views and ecommerce events.
 *
 * We send the Google Measurement ID in production and the Gtag ID in staging to ReactGA4.
 */

import { useCallback } from 'react';

import {
  getGA4Purchase,
  getRakutenLineItems,
  getPinterestLineItems,
} from './gaHelpers';
import {
  type SegmentEventType,
  type SegmentEvents,
  type SegmentIdentify,
} from './types';
import { useBranch } from './useBranch';

import {
  type GaEcommerceItemData,
  type GaEcommerceTransactionData,
} from '@/modules/checkout/types';

export function useExternalTracking() {
  const { getBranch } = useBranch();

  const init = useCallback(() => {
    window.INITIALIZED_TRACKING = true;
  }, []);

  const shouldTrack = useCallback(
    () => window.navigator.doNotTrack !== '1',
    []
  );

  const sendGTMEvent = useCallback(
    (event: unknown) => {
      if (!shouldTrack()) {
        return false;
      }
      if (!window.INITIALIZED_TRACKING) {
        init();
      }
      if (window.dataLayer) {
        return window.dataLayer.push(event);
      }
    },
    [shouldTrack, init]
  );

  /**
   *
   * @param items product items to be purchased
   * @param transaction transaction data after successful purchase
   * @returns sends the purchase and add to cart events to GA and GA4.
   */
  const sendEcommerceEvent = useCallback(
    (
      items: GaEcommerceItemData[],
      transaction: GaEcommerceTransactionData,
      eventId: string,
      tax?: string,
      buyerPurchaseCount?: number
    ) => {
      if (!shouldTrack()) {
        return false;
      }
      if (!window.INITIALIZED_TRACKING) {
        init();
      }

      const customerType = (buyerPurchaseCount ?? 0) === 0;

      const ga4Purchase = getGA4Purchase(items, transaction);
      if (window.dataLayer) {
        window.dataLayer.push({
          ecommerce: null,
        });
        return window.dataLayer.push({
          event: 'purchase',
          event_id: eventId,
          num_items: ga4Purchase.items.length,
          content_ids: ga4Purchase.items.map((item) => item.item_id),
          contents: ga4Purchase.items.map((ga4item) => ({
            id: ga4item.item_id,
            item_price: ga4item.price,
            quantity: ga4item.quantity,
          })),
          ecommerce: {
            currency: ga4Purchase.currency,
            items: ga4Purchase.items,
            shipping: ga4Purchase.shipping,
            transaction_id: ga4Purchase.transaction_id,
            value: ga4Purchase.value,
            tax: parseFloat(tax || '0.00'),
          },
          line_items: {
            rakuten: getRakutenLineItems(items),
            pinterest: getPinterestLineItems(items),
          },
          customer_type: customerType,
          rakuten_customer_type: customerType.toString(),
        });
      }
    },
    [shouldTrack, init]
  );

  async function identifyBranchUser(userId: number) {
    if (!shouldTrack()) {
      return false;
    }

    const branch = await getBranch();
    if (branch) {
      return branch.setIdentity(userId.toString());
    }
    return false;
  }

  async function logoutBranch() {
    if (!shouldTrack()) {
      return false;
    }
    const branch = await getBranch();
    if (branch) {
      return branch.logout();
    }
    return false;
  }

  async function sendBranchEvent(
    eventName: string,
    data = {},
    contentItems: Record<string, unknown>[] = [{}]
  ) {
    if (!shouldTrack()) {
      return false;
    }
    const branch = await getBranch();
    if (branch) {
      return branch.logEvent(eventName, data, contentItems);
    }
    return false;
  }

  function sendSegmentEvent(eventName: SegmentEventType, data: SegmentEvents) {
    const { analytics } = window;
    if (
      !shouldTrack() ||
      process.env.SEGMENT_ENABLED === 'false' ||
      !analytics
    ) {
      return false;
    }

    if (!window.INITIALIZED_TRACKING) {
      init();
    }

    if (window.branch?.getBrowserFingerprintId) {
      return window.branch.getBrowserFingerprintId((_, fingerprint) => {
        analytics.track(eventName, {
          ...data,
          browser_fingerprint_id: fingerprint,
        });
      });
    }

    return analytics.track(eventName, data);
  }

  function sendSegmentIdentification(userId: string, data: SegmentIdentify) {
    const { analytics } = window;

    if (
      !shouldTrack() ||
      process.env.SEGMENT_ENABLED === 'false' ||
      !analytics
    ) {
      return false;
    }
    if (!window.INITIALIZED_TRACKING) {
      init();
    }

    return analytics.identify(userId, data);
  }

  return {
    gtm: {
      sendEvent: sendGTMEvent,
      sendEcommerceEvent,
    },
    branch: {
      sendEvent: sendBranchEvent,
      identifyUser: identifyBranchUser,
      logout: logoutBranch,
    },
    segment: {
      sendEvent: sendSegmentEvent,
      identifyUser: sendSegmentIdentification,
    },
  };
}
